import { Injectable } from '@angular/core';
import { StudioSceneShape } from '../../../shape/shapes/general/root/studio-scene-shape';
import { StudioAnimation, StudioCell, StudioScene, StudioService } from '..';
import { HttpService } from '../../../../store/http/http.service';
import { first } from 'rxjs/operators';
import { StudioTextAnimationService } from './text/studio-text-animation.service';
import { StudioImageAnimationService } from './image/studio-image-animation.service';
import { LayoutAnimationFamily, LayoutAnimationStep } from './animation.types';
import { RootLayoutCell } from '../layout/layout-cell';

// ─────────────────────────────────────────────────────────────────────────────

export interface CellPosition {
  w: number;
  h: number;
  x: number;
  y: number;
  hidden?: boolean;
}

export interface AnimationStep {
  type: 'move' | 'appear' | 'disappear' | 'scale';
  to?: CellPosition;
  duration?: number;
  scale?: number;
  opacity?: number;
}

export interface AnimationNode {
  target: string;
  duration: number;
  animation?: AnimationStep;
  paralell?: AnimationNode;
  next?: AnimationNode;
}

export interface RealingMent {
  vAlign?: 'top' | 'center' | 'bottom';
  align?: 'left' | 'center' | 'right';
}

export interface SceneAnimation {
  startPosition: Record<string, CellPosition>;
  duration: number;
  playVideo?: boolean;
  next?: AnimationNode;
}

export interface AnimationData {
  case: {
    text: number | string;
    image: number | string;
    element: number | string;
  };
  animation: StudioAnimation;
}

/**
 * Animation Context - Provides all necessary context for animation generation.
 *
 * This interface captures:
 * - Client preferences (style presets)
 * - Contextual metadata (video/media information)
 * - Graphical metadata (visual properties)
 */
export interface AnimationContext {
  /** Client-defined animation preferences */
  preferences?: AnimationPreferences;

  animation?: StudioAnimation;

  /** Metadata about media elements in the scene */
  media?: MediaContext;

  /** Visual/graphical properties of scene content */
  graphics?: GraphicsContext;

  /** Scene-level information */
  scene?: SceneContext;
}

/** Client preferences for animation style */
export interface AnimationPreferences {
  /** Overall animation style preset */
  style?: 'minimal' | 'dynamic' | 'cinematic' | 'playful';

  /** Preferred animation speed */
  pacing?: 'slow' | 'normal' | 'fast';

  /** Text animation preset key */
  textPreset?: string;

  /** Image/video animation preset key */
  mediaPreset?: string;

  /** Transition style between elements */
  transitionStyle?: 'cut' | 'fade' | 'slide' | 'zoom';
}

/** Contextual metadata from video/media */
export interface MediaContext {
  /** Background video metadata */
  video?: {
    duration: number;
    position: 'background' | 'left' | 'right' | 'top' | 'bottom';
    hasMotion: boolean;
    dominantColors?: string[];
    tempo?: 'static' | 'slow' | 'moderate' | 'fast';
  };

  /** Audio/music context if available */
  audio?: {
    bpm?: number;
    hasBeat: boolean;
    mood?: 'calm' | 'energetic' | 'dramatic';
  };
}

/** Graphical metadata about visual content */
export interface GraphicsContext {
  /** Text content properties */
  text?: {
    count?: number;
    wordCount: number;
    lineCount: number;
    hasEmphasis: boolean;
    longestWordLength: number;
  };

  /** Image properties */
  image?: {
    count?: number;
    aspectRatio: number;
    dominantColors?: string[];
    hasTransparency: boolean;
  };

  /** Layout properties */
  layout?: {
    contentDensity: 'sparse' | 'balanced' | 'dense';
    primaryElement: 'text' | 'image' | 'video';
    alignment: 'left' | 'center' | 'right';
  };
  background?: {
    color: string;
    gradient?: string;
    glow?: {
      color: string;
      intensity: number;
    };
  };
}

/** Scene-level context */
export interface SceneContext {
  /** Position in the overall video sequence */
  positionIndex: number;

  /** Total number of scenes */
  totalScenes: number;

  /** Scene role in narrative */
  role?: 'intro' | 'body' | 'emphasis' | 'conclusion';

  /** Duration allocated for this scene */
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class StudioAnimationService {
  private currentTimeline: gsap.core.Timeline | null = null;

  get currentStudioSceneShape() {
    return this.studioService.currentSceneShape;
  }

  animations: Record<string, AnimationData>;
  constructor(
    private readonly httpService: HttpService,
    private readonly studioService: StudioService,
    private readonly textAnimationService: StudioTextAnimationService,
    private readonly imageAnimationService: StudioImageAnimationService,
  ) {
    this.getAnimations();
  }

  /**
   * Execute animation on a StudioSceneShape
   */

  /**
   * Apply start positions to cells
   */

  /**
   * Apply position to a cell
   */

  async getAnimations() {
    const animations = await this.httpService.httpClient
      .get(`/assets/animation/animations.json?v=${Date.now()}`)
      .pipe(first())
      .toPromise();
    this.animations = animations as Record<string, AnimationData>;
    return animations;
  }

  /**
   * Process an animation node and add to timeline
   */
  /**
   * Stop all videos in the scene
   */
  private stopVideosInScene(sceneShape: StudioSceneShape) {
    Object.entries(sceneShape.shapeCells).forEach(([key, cell]) => {
      if (key.startsWith('video:') && cell.videoShape) {
        cell.videoShape.stop();
      }
    });
  }

  /**
   * Stop current animation
   */
  stop(sceneShape?: StudioSceneShape) {
    if (this.currentTimeline) {
      this.currentTimeline.kill();
      this.currentTimeline = null;
    }
    if (sceneShape) {
      this.stopVideosInScene(sceneShape);
    }
  }

  /**
   * Pause current animation
   */
  pause(sceneShape?: StudioSceneShape) {
    if (this.currentTimeline) {
      this.currentTimeline.pause();
    }
    if (sceneShape) {
      Object.entries(sceneShape.shapeCells).forEach(([key, cell]) => {
        if (key.startsWith('video:') && cell.videoShape) {
          cell.videoShape.pause();
        }
      });
    }
  }

  /**
   * Resume current animation
   */
  resume(sceneShape?: StudioSceneShape) {
    if (this.currentTimeline) {
      this.currentTimeline.resume();
    }
    if (sceneShape) {
      Object.entries(sceneShape.shapeCells).forEach(([key, cell]) => {
        if (key.startsWith('video:') && cell.videoShape) {
          cell.videoShape.play();
        }
      });
    }
  }

  /**
   * Get default animation for a scene based on its content
   */
  getDefaultAnimation(sceneShape: StudioSceneShape): SceneAnimation {
    const { scene } = sceneShape;
    const hasText = !!scene.texts;
    const hasVideo = scene.videoInstances && scene.videoInstances.length > 0;
    const hasImage = scene.images && scene.images.length > 0;

    // Text + Video animation
    if (hasText && hasVideo) {
      return {
        startPosition: {
          'text:0': { hidden: true, w: 1, h: 1, x: 0, y: 0 },
          'video:1': { w: 1, h: 1, x: 0, y: 0 },
        },
        duration: 1.5,
        playVideo: true,
        next: {
          target: 'video:1',
          duration: 1,
          animation: {
            type: 'move',
            to: { w: 0.5, h: 1, x: 0.5, y: 0 },
            duration: 1,
          },
          paralell: {
            target: 'text:0',
            duration: 1,
            animation: { type: 'appear' },
          },
        },
      };
    }

    // Text + Image animation
    if (hasText && hasImage) {
      return {
        startPosition: {
          'text:0': { hidden: true, w: 1, h: 1, x: 0, y: 0 },
          'image:0': { w: 1, h: 1, x: 0, y: 0 },
        },
        duration: 1,
        next: {
          target: 'image:0',
          duration: 1,
          animation: {
            type: 'move',
            to: { w: 0.5, h: 1, x: 0.5, y: 0 },
            duration: 1,
          },
          paralell: {
            target: 'text:0',
            duration: 1,
            animation: { type: 'appear' },
          },
        },
      };
    }

    // Default: no animation
    return {
      startPosition: {},
      duration: 0,
    };
  }

  /**
   * Regenerate animation for the current scene based on its content and context.
   * Returns the generated animation and applies it to the scene.
   */
  regenerateAnimation(): StudioAnimation | null {
    if (this.animationIsPlaying) {
      return null;
    }
    const sceneShape = this.currentStudioSceneShape;

    if (!sceneShape) {
      return null;
    }

    // 1. Get scene context
    const context = sceneShape.getContext();
    const { graphics } = context;

    if (!graphics?.layout) {
      return null;
    }

    // 2. Determine primary element type and generate animation
    const primaryElement = graphics.layout.primaryElement;
    let animation: StudioAnimation | null = null;

    switch (primaryElement) {
      case 'text':
        // animation = this.textAnimationService.getAnimation(context);
        break;

      case 'image':
        animation = this.imageAnimationService.getAnimation(context);
        break;

      case 'video':
        // For video, create a simple appear animation
        const videoId = sceneShape.scene.videoInstances?.[0];
        if (videoId) {
          animation = {
            target: videoId,
            type: 'appear',
            duration: 1,
          };
        }
        break;
    }

    // 3. Handle mixed content (text + image or text + video)
    animation = this.combineAnimationsIfNeeded(sceneShape, context, animation);

    // 4. Apply scene role modifiers
    if (context.scene?.role === 'intro') {
      animation = this.applyIntroStyle(animation);
    } else if (context.scene?.role === 'conclusion') {
      animation = this.applyConclusionStyle(animation);
    }

    // 5. Update scene with new animation
    if (animation) {
      sceneShape.scene.animation = animation;
      sceneShape.save();
    }

    // 6. Play the animation
    if (animation) {
      this.animationIsPlaying = true;
      const timeline = sceneShape.gsapAnimate({ animation });
      // timeline.eventCallback('onComplete', () => {
      //   setTimeout(() => {
      //     this.animationIsPlaying = false;
      //     this.studioService.resetToStart();
      //   }, 500);
      // });
    }

    return animation;
  }

  /**
   * Pick one random animation from getAnimationOptions, apply it and play it.
   */
  applyRandomAnimation(): void {
    const { texts, images, videoInstances } =
      this.currentStudioSceneShape.scene;

    // if (texts?.length) {
    //   (
    //     this.currentStudioSceneShape.shapes[texts[0]] as StudioTextShape
    //   )._colorizeText();
    //   return;
    // }

    if (this.animationIsPlaying) {
      return;
    }
    const sceneShape = this.currentStudioSceneShape;
    if (!sceneShape) {
      return;
    }

    const [scene] = this.getAnimationOptions(sceneShape.scene, 1);

    const animation = scene.animation;
    if (!animation) {
      return;
    }

    sceneShape.scene.animation = animation;
    sceneShape.save();

    this.animationIsPlaying = true;
    const timeline = sceneShape.gsapAnimate({ animation });
    timeline.eventCallback('onComplete', () => {
      setTimeout(() => {
        this.animationIsPlaying = false;
        this.studioService.resetToStart();
      }, 500);
    });
  }

  animationIsPlaying = false;
  /**
   * Apply intro style modifiers to animation
   * Intro scenes typically have slower, more dramatic animations
   */
  private applyIntroStyle(
    animation: StudioAnimation | null,
  ): StudioAnimation | null {
    if (!animation) return null;

    return {
      ...animation,
      duration: animation.duration * 1.3, // Slower for intro
      delay: 0.2, // Slight delay for dramatic effect
    };
  }

  /**
   * Apply conclusion style modifiers to animation
   * Conclusion scenes typically have faster, wrapping-up animations
   */
  private applyConclusionStyle(
    animation: StudioAnimation | null,
  ): StudioAnimation | null {
    if (!animation) return null;

    return {
      ...animation,
      duration: animation.duration * 0.8, // Faster for conclusion
    };
  }

  /**
   * Combine animations for scenes with multiple element types
   */
  private combineAnimationsIfNeeded(
    sceneShape: StudioSceneShape,
    context: AnimationContext,
    primaryAnimation: StudioAnimation | null,
  ): StudioAnimation | null {
    const { scene } = sceneShape;
    const hasText = scene.texts && scene.texts.length > 0;
    const hasImage = scene.images && scene.images.length > 0;
    const hasVideo = scene.videoInstances && scene.videoInstances.length > 0;

    // Count element types
    const elementTypes = [hasText, hasImage, hasVideo].filter(Boolean).length;

    // If only one element type, return primary animation
    if (elementTypes <= 1) {
      return primaryAnimation;
    }

    const contentDensity = context.graphics?.layout?.contentDensity;

    // Handle text + image
    if (hasText && hasImage) {
      const textAnimation =
        this.textAnimationService.getTextAnimation(sceneShape);
      const imageAnimation = this.imageAnimationService.getAnimation(context);

      if (imageAnimation && textAnimation) {
        if (contentDensity === 'sparse') {
          // Sequential: image first, then text
          imageAnimation.next = {
            ...textAnimation,
            overlap: 0.3,
          };
        } else {
          // Parallel: both animate together
          // imageAnimation.paralell = textAnimation;
        }
        return imageAnimation;
      }
    }

    // Handle text + video
    if (hasText && hasVideo) {
      const textAnimation =
        this.textAnimationService.getTextAnimation(sceneShape);
      const videoId = scene.videoInstances[0];

      if (textAnimation && videoId) {
        const videoAnimation: StudioAnimation = {
          target: videoId,
          type: 'appear',
          duration: context.media?.video?.duration || 0.5,
        };

        // Video plays, then text appears
        videoAnimation.next = {
          ...textAnimation,
          overlap: 0.2,
        };
        return videoAnimation;
      }
    }

    return primaryAnimation;
  }

  // ── Layout animation steps ──────────────────────────────────────────────────

  getLayoutAnimationSteps(
    rootCell: RootLayoutCell,
    dynamicType: 'replace' | 'shift' | 'carousel' | 'fixed',
  ): LayoutAnimationStep[] {
    return rootCell.getLayoutAnimationSteps(dynamicType);
  }

  convertLayoutAnimation(
    scene: StudioScene,
    steps: LayoutAnimationStep[],
  ): StudioAnimation | null {
    if (steps.length === 0) return null;

    let root: StudioAnimation | null = null;
    let prev: StudioAnimation | null = null;
    let pendingDelay = 0;

    for (const step of steps) {
      const pos = step.position;
      let node: StudioAnimation = {
        target: step.target,
        duration: this._defaultDuration(step.type),
        type: step.type as StudioAnimation['type'],
      };

      if (step.type === 'disappear') {
        node.delay = 0.4;
      }
      if (step.type === 'appear' && step.parallel) {
        node.delay = 0.3;
      }
      if (step.overlap !== undefined) {
        node.overlap = step.overlap;
      }

      if (step.type === 'appear' && pos !== undefined) {
        node.startPosition = { cell: pos as StudioCell };
        if (step.effect?.opacity !== undefined) {
          node.startPosition.opacity = step.effect.opacity;
        }
        if (step.effect?.zoom !== undefined) {
          node.startPosition.zoom = step.effect.zoom;
        }
      }

      if (step.type == 'appear') {
        const { texts, images, videoInstances } = scene;

        if (texts?.includes(step.target)) {
          node = {
            ...node,
            ...this.textAnimationService.getOneRandomAnimation(step.target),
          };
        } else if (images?.includes(step.target)) {
          //  -- //
        } else if (videoInstances?.includes(step.target)) {
          // -- //
        }
        // const { texts, images, videoInstances } =
        //   this.currentStudioSceneShape.scene;
        // const target = this.currentStudioSceneShape.scene.shapes[step.target];
      }

      if (step.type === 'move' && pos !== undefined) {
        node.translate = { to: pos as any };
      }

      if (step.parallel && prev !== null) {
        if (step.type === 'appear') {
          const appearFinish = 0.3 + this._defaultDuration('appear');
          pendingDelay = Math.max(pendingDelay, appearFinish - prev.duration);
        }
        let tail = prev;
        while (tail.paralell) tail = tail.paralell;
        tail.paralell = node;
      } else {
        if (pendingDelay > 0) {
          node.delay = pendingDelay;
          pendingDelay = 0;
        }
        if (prev !== null) prev.next = node;
        else root = node;
        prev = node;
      }
    }

    return root;
  }

  private _defaultDuration(type: LayoutAnimationStep['type']): number {
    switch (type) {
      case 'appear':
        return 0.8;
      case 'disappear':
        return 0.4;
      case 'move':
        return 0.5;
    }
  }

  // ────────────────────────────────────────────────────────────────────────────

  getRandomFromArray(array: any[]) {
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
  }

  getNewAnimationOptions(scene: StudioScene) {
    const context = this.currentStudioSceneShape.getContext();
    // -- // -- // -- // -- // -- // -- // -- // -- // -- //
  }

  getAnimationOptions(scene: StudioScene, cnt = 3): StudioScene[] {
    const { texts, images, videoInstances } = scene;

    const textLength = texts?.length || 0;
    const imageLength = images?.length || 0;
    const videoLength = videoInstances?.length || 0;

    if (textLength === 1 && !imageLength && !videoLength) {
      return this.textAnimationService
        .getAnimationOptions(texts[0], cnt)
        .map(animation => ({
          ...scene,
          animation,
        }));
    }

    const families: Array<LayoutAnimationFamily> = [
      'replace',
      'shift',
      // 'carousel',
      'fixed',
    ];

    return families.map(type => {
      const rootCell = new RootLayoutCell(scene, scene.layoutDefinition);
      const steps = this.getLayoutAnimationSteps(rootCell, type);
      const animation = this.convertLayoutAnimation(scene, steps);
      return { ...scene, animation: animation ?? undefined };
    });
  }
}
